import moment from 'moment'
import { fetchCompany } from '@/api/parameters'
import { moneda } from '@/utils/filtros'
import numeral from 'numeral'
import { lineBreakdownSection } from './lineBreakdownPdf'

export async function generateCommercialDashboardPdf(payload) {
  const pdfMake = require("pdfmake/build/pdfmake.js")
  const pdfFonts = require("pdfmake/build/vfs_fonts.js")
  pdfMake.vfs = pdfFonts.pdfMake.vfs

  // Datos de la empresa
  let company = payload.company || {}
  try {
    if (!company.name) {
      company = await fetchCompany()
    }
  } catch (e) {
    company = {}
  }

  const countryCode = company.country_code || 'cl'
  const companyName = company.fantasy || company.name || 'Runflow ERP'
  const companyRut = company.rut || ''
  const timezone = payload.serverTimezone || company.current_time_zone || 'America/Santiago'

  const formatMoney = (val) => moneda(val, countryCode)

  const startDateFormatted = payload.startDate ? moment(payload.startDate).format('DD/MM/YYYY') : '—'
  const endDateFormatted = payload.endDate ? moment(payload.endDate).format('DD/MM/YYYY') : '—'
  const lineName = payload.lineName || 'Todas las líneas'
  const lines = payload.lines || []
  const periodLabel = `${startDateFormatted} al ${endDateFormatted} · Línea: ${lineName}`

  const now = moment()
  const emissionDate = now.format('DD/MM/YYYY HH:mm')

  const kpis = payload.kpis || {}
  const marginBands = payload.marginBands || {}
  const sellers = payload.sellers || []
  const customers = payload.customers || []
  const worstOrders = payload.worstMarginOrders || []
  const totalSales = Number(kpis.confirmed || 0)

  const deltaLabel = (val) => {
    if (val == null) return '—'
    const sign = val > 0 ? '+' : ''
    return `${sign}${val}%`
  }

  // Cálculos de salud de margen
  const healthy = marginBands.healthy || { count: 0, sales: 0 }
  const ok = marginBands.ok || { count: 0, sales: 0 }
  const tight = marginBands.tight || { count: 0, sales: 0 }
  const loss = marginBands.loss || { count: 0, sales: 0 }
  const totalBandSales = (healthy.sales || 0) + (ok.sales || 0) + (tight.sales || 0) + (loss.sales || 0) || totalSales || 1

  const pct = (part, tot) => ((Number(part || 0) / Math.max(1, tot)) * 100).toFixed(1)

  // Document Definition
  const dd = {
    pageSize: 'LETTER',
    pageOrientation: 'landscape',
    pageMargins: [28, 22, 28, 22],
    footer: function(currentPage, pageCount) {
      return {
        margin: [28, 0, 28, 0],
        columns: [
          { text: 'RUNFLOW ERP · SISTEMA DE GESTIÓN INTELIGENTE', fontSize: 7, color: '#94A3B8', bold: true },
          { text: `Página ${currentPage} de ${pageCount}`, alignment: 'right', fontSize: 7, color: '#94A3B8', bold: true }
        ]
      }
    },
    content: [
      // ==========================================
      // PÁGINA 1: RESUMEN EJECUTIVO (ONE-PAGER)
      // ==========================================

      // 1. Header Banner - Midnight Slate (#0F172A)
      {
        table: {
          widths: ['*'],
          body: [
            [
              {
                fillColor: '#0F172A',
                border: [false, false, false, false],
                margin: [16, 12, 16, 12],
                columns: [
                  {
                    width: '*',
                    stack: [
                      {
                        text: 'RUNFLOW · INFORME EJECUTIVO DE VENTAS Y RENTABILIDAD',
                        fontSize: 8,
                        bold: true,
                        color: '#00A650',
                        characterSpacing: 1.5,
                        margin: [0, 0, 0, 3]
                      },
                      {
                        text: `${companyName}`,
                        fontSize: 16,
                        bold: true,
                        color: '#FFFFFF',
                        margin: [0, 0, 0, 3]
                      },
                      {
                        text: `${companyRut ? 'RUT: ' + companyRut + '  ·  ' : ''}Rango Auditado: ${periodLabel}`,
                        fontSize: 9,
                        color: '#CBD5E1'
                      }
                    ]
                  },
                  {
                    width: 220,
                    alignment: 'right',
                    stack: [
                      {
                        text: 'CONFIDENCIAL · COMITÉ EJECUTIVO',
                        fontSize: 7.5,
                        bold: true,
                        color: '#FCA5A5',
                        margin: [0, 0, 0, 6]
                      },
                      {
                        text: `Emisión: ${emissionDate} hrs`,
                        fontSize: 8,
                        color: '#94A3B8',
                        margin: [0, 0, 0, 2]
                      },
                      {
                        text: `Zona Horaria: ${timezone}`,
                        fontSize: 7.5,
                        color: '#64748B'
                      }
                    ]
                  }
                ]
              }
            ]
          ]
        },
        margin: [0, 0, 0, 10],
        layout: {
          paddingLeft: () => 0,
          paddingRight: () => 0,
          paddingTop: () => 0,
          paddingBottom: () => 0
        }
      },

      // 2. Row of 4 Hero KPIs
      {
        table: {
          widths: ['25%', '25%', '25%', '25%'],
          body: [
            [
              // Card 1: North Star
              {
                fillColor: '#0F172A',
                border: [false, false, false, false],
                margin: [10, 8, 10, 8],
                stack: [
                  { text: 'VENTA CONFIRMADA (OT)', fontSize: 7, bold: true, color: '#00A650', characterSpacing: 1 },
                  { text: formatMoney(kpis.confirmed || 0), fontSize: 15, bold: true, color: '#FFFFFF', margin: [0, 3, 0, 3] },
                  {
                    text: `${kpis.confirmed_count || 0} OTs  ·  ${deltaLabel(kpis.vs_previous_pct)} vs anterior`,
                    fontSize: 7.5,
                    color: '#94A3B8'
                  }
                ]
              },
              // Card 2: Cotizado
              {
                fillColor: '#F8FAFC',
                border: [false, false, false, false],
                margin: [10, 8, 10, 8],
                stack: [
                  { text: 'TOTAL COTIZADO', fontSize: 7, bold: true, color: '#64748B', characterSpacing: 1 },
                  { text: formatMoney(kpis.quoted || 0), fontSize: 15, bold: true, color: '#0F172A', margin: [0, 3, 0, 3] },
                  { text: `${kpis.count || 0} cotizaciones emitidas`, fontSize: 7.5, color: '#64748B' }
                ]
              },
              // Card 3: Efectividad
              {
                fillColor: '#F8FAFC',
                border: [false, false, false, false],
                margin: [10, 8, 10, 8],
                stack: [
                  { text: 'EFECTIVIDAD DE CIERRE', fontSize: 7, bold: true, color: '#64748B', characterSpacing: 1 },
                  { text: `${(Number(kpis.conversion || 0)).toFixed(1)}%`, fontSize: 15, bold: true, color: '#0284C7', margin: [0, 3, 0, 3] },
                  { text: `Pipeline activo: ${formatMoney(kpis.pipeline != null ? kpis.pipeline : (kpis.quoted_pipeline || 0))}`, fontSize: 7.5, color: '#64748B' }
                ]
              },
              // Card 4: Margen
              {
                fillColor: '#F8FAFC',
                border: [false, false, false, false],
                margin: [10, 8, 10, 8],
                stack: [
                  { text: 'MARGEN TEÓRICO PONDERADO', fontSize: 7, bold: true, color: '#64748B', characterSpacing: 1 },
                  {
                    text: `${kpis.confirmed_margin != null ? (Math.abs(kpis.confirmed_margin) <= 1 ? (kpis.confirmed_margin * 100).toFixed(1) : Number(kpis.confirmed_margin).toFixed(1)) : (kpis.margin_pct != null ? Number(kpis.margin_pct).toFixed(1) : '0.0')}%`,
                    fontSize: 15,
                    bold: true,
                    color: '#0F172A',
                    margin: [0, 3, 0, 3]
                  },
                  { text: `Utilidad: ${formatMoney(kpis.confirmed_profit != null ? kpis.confirmed_profit : (kpis.margin_amount || 0))}`, fontSize: 7.5, color: '#64748B' }
                ]
              }
            ]
          ]
        },
        margin: [0, 0, 0, 10],
        layout: {
          defaultBorder: false,
          hLineWidth: () => 0,
          vLineWidth: () => 0,
          paddingLeft: () => 3,
          paddingRight: () => 3,
          paddingTop: () => 0,
          paddingBottom: () => 0
        }
      },

      ...lineBreakdownSection({
        title: 'VENTA POR LÍNEA DE NEGOCIO (TODAS LAS LÍNEAS)',
        rows: lines,
        selectedName: lineName,
        columns: [
          { header: 'LÍNEA', value: (l) => l.name },
          { header: 'COTIZADO', width: 85, align: 'right', value: (l) => formatMoney(l.quoted || 0) },
          { header: 'VENDIDO', width: 85, align: 'right', bold: true, value: (l) => formatMoney(l.confirmed || 0) },
          { header: 'CONVERSIÓN', width: 60, align: 'right', value: (l) => `${Number(l.conversion || 0).toFixed(1)}%` },
          { header: 'MARGEN', width: 55, align: 'right', value: (l) => `${(Number(l.confirmed_margin || 0) * 100).toFixed(1)}%` },
          { header: 'M² VENDIDOS', width: 70, align: 'right', value: (l) => numeral(l.confirmed_m2 || 0).format('0,0.0') },
          { header: 'UNID. VENDIDAS', width: 70, align: 'right', value: (l) => numeral(l.confirmed_qty || 0).format('0,0') },
          { header: 'COTIZ. / OTs', width: 60, align: 'center', value: (l) => `${l.count || 0} / ${l.won || 0}` }
        ]
      }),

      // 3. Middle Area: Evolución Temporal + Salud de Margen
      {
        columns: [
          // Left: Chart Image (width 430 pt)
          {
            width: 430,
            stack: [
              {
                text: 'EVOLUCIÓN TEMPORAL DE VENTAS Y COTIZACIONES',
                fontSize: 8,
                bold: true,
                color: '#475569',
                characterSpacing: 0.8,
                margin: [0, 0, 0, 4]
              },
              payload.chartImageBase64 ? {
                image: payload.chartImageBase64,
                width: 430,
                height: 165
              } : {
                table: {
                  widths: ['*'],
                  body: [[{ text: 'Gráfico no disponible para exportación', alignment: 'center', margin: [0, 70, 0, 70], color: '#94A3B8' }]]
                }
              }
            ]
          },
          // Gap: 15 pt
          { width: 15, text: '' },
          // Right: Salud de Margen (width 290 pt)
          {
            width: 290,
            stack: [
              {
                text: 'DISTRIBUCIÓN POR SALUD DE MARGEN TEÓRICO',
                fontSize: 8,
                bold: true,
                color: '#475569',
                characterSpacing: 0.8,
                margin: [0, 0, 0, 4]
              },
              {
                table: {
                  widths: ['*', 70, 80],
                  body: [
                    [
                      { text: 'TRAMO DE RENTABILIDAD', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC' },
                      { text: 'OTs', fontSize: 7, bold: true, color: '#64748B', alignment: 'center', fillColor: '#F8FAFC' },
                      { text: 'MONTO (% DEL TOTAL)', fontSize: 7, bold: true, color: '#64748B', alignment: 'right', fillColor: '#F8FAFC' }
                    ],
                    [
                      { text: '● Sano (≥ 30%)', fontSize: 7.5, bold: true, color: '#047857' },
                      { text: `${healthy.count || 0} OT`, fontSize: 7.5, alignment: 'center', color: '#047857' },
                      { text: `${formatMoney(healthy.sales || 0)} (${pct(healthy.sales, totalBandSales)}%)`, fontSize: 7.5, bold: true, alignment: 'right', color: '#047857' }
                    ],
                    [
                      { text: '● Aceptable (15–30%)', fontSize: 7.5, bold: true, color: '#0369A1' },
                      { text: `${ok.count || 0} OT`, fontSize: 7.5, alignment: 'center', color: '#0369A1' },
                      { text: `${formatMoney(ok.sales || 0)} (${pct(ok.sales, totalBandSales)}%)`, fontSize: 7.5, bold: true, alignment: 'right', color: '#0369A1' }
                    ],
                    [
                      { text: '● Ajustado (0–15%)', fontSize: 7.5, bold: true, color: '#B45309' },
                      { text: `${tight.count || 0} OT`, fontSize: 7.5, alignment: 'center', color: '#B45309' },
                      { text: `${formatMoney(tight.sales || 0)} (${pct(tight.sales, totalBandSales)}%)`, fontSize: 7.5, bold: true, alignment: 'right', color: '#B45309' }
                    ],
                    [
                      { text: '● Pérdida (< 0%)', fontSize: 7.5, bold: true, color: '#BE123C' },
                      { text: `${loss.count || 0} OT`, fontSize: 7.5, alignment: 'center', color: '#BE123C' },
                      { text: `${formatMoney(loss.sales || 0)} (${pct(loss.sales, totalBandSales)}%)`, fontSize: 7.5, bold: true, alignment: 'right', color: '#BE123C' }
                    ]
                  ]
                },
                layout: {
                  hLineWidth: () => 0.5,
                  vLineWidth: () => 0,
                  hLineColor: () => '#E2E8F0',
                  paddingLeft: () => 6,
                  paddingRight: () => 6,
                  paddingTop: () => 5,
                  paddingBottom: () => 5
                }
              },
              // Micro Ticker Pulso de Hoy
              {
                margin: [0, 8, 0, 0],
                table: {
                  widths: ['*'],
                  body: [
                    [
                      {
                        fillColor: '#F1F5F9',
                        border: [false, false, false, false],
                        margin: [8, 6, 8, 6],
                        columns: [
                          { text: 'PULSO DE HOY:', fontSize: 7.5, bold: true, color: '#0F172A', width: 85 },
                          { text: formatMoney(kpis.today_confirmed || 0), fontSize: 8.5, bold: true, color: '#00A650', width: '*' },
                          { text: `${kpis.today_count || 0} OT emitidas hoy`, fontSize: 7, color: '#64748B', alignment: 'right', width: 100 }
                        ]
                      }
                    ]
                  ]
                },
                layout: {
                  defaultBorder: false,
                  hLineWidth: () => 0,
                  vLineWidth: () => 0
                }
              }
            ]
          }
        ],
        margin: [0, 0, 0, 10]
      },

      // 4. Bottom Row: Top 5 Ejecutivos y Top 5 Clientes (Zero overflow, exact fit on Page 1)
      {
        columns: [
          // Table Left: Top 5 Sellers
          {
            width: 360,
            stack: [
              {
                text: 'TOP EJECUTIVOS COMERCIALES (VENTA CONFIRMADA)',
                fontSize: 8,
                bold: true,
                color: '#475569',
                characterSpacing: 0.8,
                margin: [0, 0, 0, 4]
              },
              {
                table: {
                  headerRows: 1,
                  dontBreakRows: true,
                  widths: [16, '*', 45, 75, 45],
                  body: [
                    [
                      { text: '#', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'center' },
                      { text: 'EJECUTIVO', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC' },
                      { text: 'OTs', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'center' },
                      { text: 'MONTO NETO', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'right' },
                      { text: '% PART.', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'right' }
                    ],
                    ...(sellers.slice(0, 5).length ? sellers.slice(0, 5).map((s, idx) => [
                      { text: String(idx + 1), fontSize: 7.5, color: '#64748B', alignment: 'center' },
                      { text: s.name || 'Sin nombre', fontSize: 7.5, bold: true, color: '#0F172A' },
                      { text: String(s.count || 0), fontSize: 7.5, alignment: 'center', color: '#475569' },
                      { text: formatMoney(s.total || 0), fontSize: 7.5, bold: true, alignment: 'right', color: '#0F172A' },
                      { text: `${pct(s.total, totalSales)}%`, fontSize: 7.5, alignment: 'right', color: '#64748B' }
                    ]) : [
                      [{ text: 'Sin registros de ejecutivos', colSpan: 5, alignment: 'center', fontSize: 7.5, color: '#94A3B8' }, {}, {}, {}, {}]
                    ])
                  ]
                },
                layout: {
                  hLineWidth: () => 0.5,
                  vLineWidth: () => 0,
                  hLineColor: () => '#E2E8F0',
                  paddingLeft: () => 4,
                  paddingRight: () => 4,
                  paddingTop: () => 4,
                  paddingBottom: () => 4
                }
              }
            ]
          },
          // Gap: 15 pt
          { width: 15, text: '' },
          // Table Right: Top 5 Customers
          {
            width: 360,
            stack: [
              {
                text: 'TOP CLIENTES PRINCIPALES (VENTA CONFIRMADA)',
                fontSize: 8,
                bold: true,
                color: '#475569',
                characterSpacing: 0.8,
                margin: [0, 0, 0, 4]
              },
              {
                table: {
                  headerRows: 1,
                  dontBreakRows: true,
                  widths: [16, '*', 45, 75, 45],
                  body: [
                    [
                      { text: '#', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'center' },
                      { text: 'CLIENTE', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC' },
                      { text: 'OTs', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'center' },
                      { text: 'MONTO NETO', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'right' },
                      { text: '% PART.', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'right' }
                    ],
                    ...(customers.slice(0, 5).length ? customers.slice(0, 5).map((c, idx) => [
                      { text: String(idx + 1), fontSize: 7.5, color: '#64748B', alignment: 'center' },
                      { text: c.name || 'Sin nombre', fontSize: 7.5, bold: true, color: '#0F172A' },
                      { text: String(c.count || 0), fontSize: 7.5, alignment: 'center', color: '#475569' },
                      { text: formatMoney(c.total || 0), fontSize: 7.5, bold: true, alignment: 'right', color: '#0F172A' },
                      { text: `${pct(c.total, totalSales)}%`, fontSize: 7.5, alignment: 'right', color: '#64748B' }
                    ]) : [
                      [{ text: 'Sin registros de clientes', colSpan: 5, alignment: 'center', fontSize: 7.5, color: '#94A3B8' }, {}, {}, {}, {}]
                    ])
                  ]
                },
                layout: {
                  hLineWidth: () => 0.5,
                  vLineWidth: () => 0,
                  hLineColor: () => '#E2E8F0',
                  paddingLeft: () => 4,
                  paddingRight: () => 4,
                  paddingTop: () => 4,
                  paddingBottom: () => 4
                }
              }
            ]
          }
        ]
      },

      // ========================================================
      // PÁGINA 2: ANEXO DE AUDITORÍA DETALLADA (PAGE BREAK FORZADO)
      // ========================================================
      ...(sellers.length > 5 || customers.length > 5 || worstOrders.length ? [
        { text: '', pageBreak: 'before' },
        // Secondary Header for Annex
        {
          table: {
            widths: ['*'],
            body: [
              [
                {
                  fillColor: '#F8FAFC',
                  border: [false, false, false, false],
                  margin: [12, 8, 12, 8],
                  columns: [
                    {
                      text: 'ANEXO DE AUDITORÍA COMERCIAL DETALLADA',
                      fontSize: 10,
                      bold: true,
                      color: '#0F172A'
                    },
                    {
                      text: `${companyName} · Período: ${periodLabel}`,
                      fontSize: 8,
                      color: '#64748B',
                      alignment: 'right'
                    }
                  ]
                }
              ]
            ]
          },
          margin: [0, 0, 0, 12],
          layout: {
            paddingLeft: () => 0,
            paddingRight: () => 0,
            paddingTop: () => 0,
            paddingBottom: () => 0
          }
        },

        // Detailed Sellers Table
        {
          text: 'DESGLOSE COMPLETO POR EJECUTIVO COMERCIAL',
          fontSize: 8.5,
          bold: true,
          color: '#475569',
          characterSpacing: 0.5,
          margin: [0, 0, 0, 4]
        },
        {
          table: {
            headerRows: 1,
            dontBreakRows: true,
            widths: [20, '*', 70, 120, 80],
            body: [
              [
                { text: '#', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'center' },
                { text: 'EJECUTIVO COMERCIAL', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#F8FAFC' },
                { text: 'CANTIDAD OTs', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'center' },
                { text: 'VENTA CONFIRMADA NETO', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'right' },
                { text: '% DE LA VENTA', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'right' }
              ],
              ...sellers.map((s, idx) => [
                { text: String(idx + 1), fontSize: 7.5, color: '#64748B', alignment: 'center' },
                { text: s.name || 'Sin nombre', fontSize: 7.5, bold: true, color: '#0F172A' },
                { text: String(s.count || 0), fontSize: 7.5, alignment: 'center', color: '#475569' },
                { text: formatMoney(s.total || 0), fontSize: 7.5, bold: true, alignment: 'right', color: '#0F172A' },
                { text: `${pct(s.total, totalSales)}%`, fontSize: 7.5, alignment: 'right', color: '#64748B' }
              ])
            ]
          },
          layout: {
            hLineWidth: () => 0.5,
            vLineWidth: () => 0,
            hLineColor: () => '#E2E8F0',
            paddingLeft: () => 6,
            paddingRight: () => 6,
            paddingTop: () => 4,
            paddingBottom: () => 4
          },
          margin: [0, 0, 0, 14]
        },

        // Detailed Customers Table (Top 15)
        {
          text: 'DESGLOSE DE CLIENTES PRINCIPALES',
          fontSize: 8.5,
          bold: true,
          color: '#475569',
          characterSpacing: 0.5,
          margin: [0, 0, 0, 4]
        },
        {
          table: {
            headerRows: 1,
            dontBreakRows: true,
            widths: [20, '*', 70, 120, 80],
            body: [
              [
                { text: '#', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'center' },
                { text: 'CLIENTE', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#F8FAFC' },
                { text: 'CANTIDAD OTs', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'center' },
                { text: 'VENTA CONFIRMADA NETO', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'right' },
                { text: '% DE LA VENTA', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'right' }
              ],
              ...customers.slice(0, 15).map((c, idx) => [
                { text: String(idx + 1), fontSize: 7.5, color: '#64748B', alignment: 'center' },
                { text: c.name || 'Sin nombre', fontSize: 7.5, bold: true, color: '#0F172A' },
                { text: String(c.count || 0), fontSize: 7.5, alignment: 'center', color: '#475569' },
                { text: formatMoney(c.total || 0), fontSize: 7.5, bold: true, alignment: 'right', color: '#0F172A' },
                { text: `${pct(c.total, totalSales)}%`, fontSize: 7.5, alignment: 'right', color: '#64748B' }
              ])
            ]
          },
          layout: {
            hLineWidth: () => 0.5,
            vLineWidth: () => 0,
            hLineColor: () => '#E2E8F0',
            paddingLeft: () => 6,
            paddingRight: () => 6,
            paddingTop: () => 4,
            paddingBottom: () => 4
          }
        }
      ] : [])
    ]
  }

  const fileName = `Reporte_Ejecutivo_Comercial_${payload.startDate || 'inicio'}_a_${payload.endDate || 'fin'}.pdf`
  pdfMake.createPdf(dd).download(fileName)
}
